import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Row, Col } from 'antd';
import {
  ButtonCommon,
  ErrorMessage,
  PrefixedInput,
  showAlertNotification,
  InputCommon,
  LabelWithRequired,
} from 'components/common';
import { AddMaterialModalProps } from 'types/common';
const { TextArea } = Input;

const AddMaterialModal: React.FC<AddMaterialModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  onGenerate,
  form,
  editMode = false,
  materialData = null,
  isSubmitting = false,
  isCopyMode = false,
}) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [codePrefix, setCodePrefix] = useState<string>('');
  const [genCode, setGenCode] = useState<string>('');

  useEffect(() => {
    if (genCode) {
      const prefix = genCode.substring(0, 6);
      setCodePrefix(prefix);
      form.setFieldsValue({ code: genCode });
    }
    if (materialData?.code && !isCopyMode) {
      const prefix = materialData.code.substring(0, 6);
      setCodePrefix(prefix);
    }
  }, [materialData?.code, genCode, form, isCopyMode]);

  useEffect(() => {
    const fetchCode = async () => {
      if (isOpen && !modalOpened && onGenerate) {
        setModalOpened(true);

        // Generate code for new materials and copy mode
        if (!editMode || isCopyMode) {
          const code = await onGenerate();
          setGenCode(code);
        }
      }

      if (!isOpen) {
        setModalOpened(false);
      }
    };
    fetchCode();
  }, [isOpen, editMode, onGenerate, modalOpened, isCopyMode]);

  useEffect(() => {
    if (isOpen) {
      console.log('Material data:', materialData, 'editMode:', editMode, 'isCopyMode:', isCopyMode);

      if (editMode && materialData && !isCopyMode) {
        // Edit mode - load existing data
        console.log('Edit material data:', materialData);
        form.setFieldsValue({
          name: materialData.name,
          code: materialData.code,
          salePrice: materialData.salePrice,
          costPrice: materialData.costPrice,
          quantity: materialData.quantity,
          isActive: materialData.isActive,
          description: materialData.description,
        });
      } else if (isCopyMode && materialData) {
        // Copy mode - load data with modified name and clear code
        console.log('Copy material data:', materialData);
        form.setFieldsValue({
          name: materialData.name + ' (Bản sao)',
          code: '', // Code will be generated
          salePrice: materialData.salePrice,
          costPrice: materialData.costPrice,
          quantity: materialData.quantity,
          isActive: materialData.isActive,
          description: materialData.description,
        });
      } else if (!editMode && !isCopyMode) {
        // New material mode
        form.resetFields();
        form.setFieldsValue({
          isActive: true,
          costPrice: 0,
          salePrice: 0,
          quantity: 0,
        });
      }
    }
  }, [isOpen, form, editMode, materialData, isCopyMode]);

  const getModalTitle = () => {
    if (isCopyMode) {
      return 'SAO CHÉP NGUYÊN LIỆU';
    }
    if (editMode) {
      return 'CHỈNH SỬA NGUYÊN LIỆU';
    }
    return 'THÊM NGUYÊN LIỆU MỚI';
  };

  const getModalDescription = () => {
    if (isCopyMode) {
      return 'Vui lòng kiểm tra và chỉnh sửa thông tin của nguyên liệu được sao chép';
    }
    if (editMode) {
      return 'Vui lòng chỉnh sửa thông tin của nguyên liệu theo nhu cầu của bạn';
    }
    return 'Vui lòng điền đầy đủ thông tin của nguyên liệu mới';
  };

  const getSubmitButtonText = () => {
    if (isCopyMode) {
      return 'Tạo bản sao';
    }
    if (editMode) {
      return 'Cập nhật';
    }
    return 'Thêm mới';
  };

  return (
    <Modal
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[18px] font-bold leading-[24px]'>{getModalTitle()}</h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>{getModalDescription()}</p>
        </div>
      }
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={1255}
      centered
      className='staff-modal'
      destroyOnClose
      closeIcon={null}
      styles={{
        wrapper: {
          alignItems: 'center',
          paddingTop: '10vh',
        },
      }}
    >
      <div className='px-[24px] py-[12] mb-[12px]'>
        <Form
          form={form}
          requiredMark={false}
          layout='vertical'
          className='bg-white rounded-lg py-[24px] px-[48px] font-medium text-[14px] leading-[20px]'
        >
          <Row gutter={[24, 0]}>
            <Col span={16}>
              <Form.Item
                label={<LabelWithRequired label='Tên nguyên liệu' />}
                name={'name'}
                rules={[
                  { required: true, message: <ErrorMessage message='Tên nguyên liệu là bắt buộc!' /> },
                  { max: 100, message: <ErrorMessage message='Tên nguyên liệu không được vượt quá 100 ký tự!' /> },
                ]}
              >
                <InputCommon
                  className='!border-0 !pl-0 rounded-none font-bold !text-[20px] !leading-[24px] !border-b-[1px] border-[var(--textBlack12)] !transition-none !shadow-none'
                  placeholder='Nhập vào tên nguyên liệu'
                  radius='none'
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Mã' />}
                name='code'
                rules={[
                  { required: true, message: <ErrorMessage message='Vui lòng nhập mã nguyên liệu!' /> },
                  { max: 20, message: <ErrorMessage message='Mã nguyên liệu không được vượt quá 20 ký tự!' /> },
                  {
                    pattern: /^[a-zA-Z0-9_]+$/,
                    message: <ErrorMessage message='Mã nguyên liệu chỉ được chứa chữ cái, số và dấu gạch dưới!' />,
                  },
                ]}
              >
                <PrefixedInput
                  prefix={codePrefix}
                  maxEditableLength={6}
                  placeholder='Nhập mã'
                  disabled={editMode && !isCopyMode} // Enable in copy mode
                  className='!text-[14px] !leading-[20px] !h-[38px]'
                  inputProps={{
                    suffix: (!editMode || isCopyMode) && (
                      <span
                        className='text-[var(--primaryColor500)] cursor-pointer'
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onGenerate) {
                            try {
                              const code = await onGenerate();
                              if (code && code.length >= 6) {
                                setCodePrefix(code.substring(0, 6));
                                form.setFieldValue('code', code);
                              }
                            } catch (error) {
                              console.error('Error generating code:', error);
                            }
                          }
                        }}
                      >
                        Generate
                      </span>
                    ),
                    hardSize: 'md2',
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Số lượng' />}
                name={'quantity'}
                rules={[{ required: true, message: <ErrorMessage message='Vui lòng nhập số lượng!' /> }]}
              >
                <InputCommon placeholder='Nhập số lượng' hardSize='md2' type='money' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Giá nhập' />}
                name={'costPrice'}
                rules={[{ required: true, message: <ErrorMessage message='Vui lòng nhập giá nhập!' /> }]}
              >
                <InputCommon placeholder='Nhập giá nhập' hardSize='md2' type='money' />
              </Form.Item>
            </Col>
            {
              <Col span={8}>
                <Form.Item
                  label={<LabelWithRequired label='Giá bán' />}
                  name='salePrice'
                  rules={[{ required: true, message: <ErrorMessage message='Vui lòng nhập giá bán!' /> }]}
                >
                  <InputCommon hardSize='md2' type='money' placeholder='Nhập vào giá bán' />
                </Form.Item>
              </Col>
            }

            <Col span={24}>
              <Form.Item
                className='font-normal'
                label='Mô tả'
                name='description'
                rules={[{ max: 1000, message: <ErrorMessage message='Mô tả không được vượt quá 1000 ký tự!' /> }]}
              >
                <TextArea rows={4} placeholder='Nhập vào mô tả...' />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className='flex justify-center gap-3 mt-6'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </ButtonCommon>
        <ButtonCommon
          color='primary'
          size='md'
          radius='rounded'
          loading={isSubmitting}
          onClick={() => {
            form
              .validateFields()
              .then(onSubmit)
              .catch((errorInfo) => {
                showAlertNotification({
                  type: 'warning',
                  title: 'Thông báo',
                  message: 'Vui lòng nhập đúng thông tin nguyên liệu',
                });
                console.log('Validation failed:', errorInfo);
              });
          }}
        >
          {getSubmitButtonText()}
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default AddMaterialModal;
